import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import validate from "../utilities/validate";
import Label from "./label.jsx";

/**
 * @typedef {import('../../types').showValidation} showValidation
 */

/**
 * @typedef {object} SelectProps - The props for the component
 * @property {object[]} items - The items for the checkboxes
 * @property {string} componentId - The id of the component
 * @property {object} validation - The validation for the form element
 * @property {showValidation} showValidation - Whether the component is highlighted or not
 */

/**
 * Renders a HTML Select element with multiple options
 * @type {React.FC<SelectProps>}
 * @returns {React.ReactElement} React function component
 */
function Select({ component, showValidation }) {
  const {
    responses,
    validation,
    componentId,
    label: componentLabel,
  } = component;


  const responsesWithDefault = [
    {
      label: "Select an Answer",
      disabled: true,
      responseId: -1,
    },
    ...responses,
  ];

  const [selectedValue, setSelectedValue] = useState(responsesWithDefault[0].responseId);

  
  /**
   *
   * @param {React.SyntheticEvent<HTMLSelectElement>} event - the event
   * @returns {void}
   */
 function updateValidation(event) {
   const { value } = event.currentTarget;
   const { isValid, key = "" } = validate("select", value, validation);
   showValidation(isValid, key, value);
   setSelectedValue(value);
  }

  return (
    <>
      {componentLabel && (
        <Label
          value={componentLabel}
          htmlFor={componentId}
          showAsterisk={validation?.required ?? false}
          classes="fr-mb-2"
        />
      )}
      <select
        className="fr-block fr-w-full fr-mb-4 sm:fr-w-40 fr-rounded-md fr-border fr-border-zinc-300 fr-bg-white fr-py-2 fr-px-3 fr-text-zinc-500 fr-shadow-sm focus-visible:fr-border-primary-500 focus-visible:fr-outline-none focus-visible:fr-ring-primary-500 sm:fr-text-sm"
        onChange={(event) => updateValidation(event)}
        value={selectedValue}
        required={validation.required}
        id={componentId}
        data-testvalue={selectedValue}
      >
        {responsesWithDefault.map(({ responseId, label, disabled = false }) => {
          const uuid = uuidv4();
          return (
            <option 
              key={uuid} 
              value={responseId} 
              disabled={disabled}
            >
              {label}
            </option>
          );
        })}
      </select>
    </>
  );
}

export default Select;